/* ============================================================
   Agapella Design System — global.css

   Source of truth: docs/agapella-design-system/project/colors_and_type.css
   --ag-* vars are emitted live by ThemeContext from the resolved JS palette
   (single source of truth). Literal fallbacks below keep visuals correct in
   the pre-hydration window before JS sets them (prevents theme flash).

   Aesthetic: warm, photo-led, monastic chic. One accent (Solar #E98245).
   Two near-blacks: obsidian #00121D (text) + charcoal #0F1419 (alt surfaces).
   ============================================================ */

/* CORE-1118 — the approved typography is global across Web. */
@font-face {
  font-family: 'Lora';
  src: url('/fonts/lora/Lora-Medium.woff2') format('woff2');
  font-style: normal;
  font-weight: 500;
  font-display: swap;
}
@font-face {
  font-family: 'Nunito Sans';
  src: url('/fonts/nunito-sans/NunitoSans-Regular.woff2') format('woff2');
  font-style: normal;
  font-weight: 400;
  font-display: swap;
}
@font-face {
  font-family: 'Nunito Sans';
  src: url('/fonts/nunito-sans/NunitoSans-SemiBold.woff2') format('woff2');
  font-style: normal;
  font-weight: 600;
  font-display: swap;
}
@font-face {
  font-family: 'Nunito Sans';
  src: url('/fonts/nunito-sans/NunitoSans-Bold.woff2') format('woff2');
  font-style: normal;
  font-weight: 700;
  font-display: swap;
}

/* CORE-110 — dots-masking font for password fields on Safari/WebKit.
   Safari's iCloud Keychain detects a "password" field via `type=password`
   OR `-webkit-text-security`, then auto-fills + LOCKS the login pair so the
   user can't type (confirmed via /login-test: the masked variant locks, the
   plaintext variant types). A masking FONT is invisible to that detection —
   Safari sees an ordinary text field — while the user still sees discs. We
   render the password as `type=text` + this font on Safari only. From the
   MIT `text-security` project. */
@font-face {
  font-family: 'text-security-disc';
  src: url('/fonts/text-security-disc.woff2') format('woff2');
  font-display: swap;
}

:root {
  --ag-font-brand: 'Lora', Georgia, 'Times New Roman', serif;
  --ag-font-ui:    'Nunito Sans', 'Helvetica Neue', Arial, sans-serif;
  /* Compatibility aliases resolve to UI. Display text must opt into the
     explicit brand token, keeping Lora out of forms/navigation/buttons. */
  --font-main:    var(--ag-font-ui);
  --font-display: var(--ag-font-ui);
  --font-text:    var(--ag-font-ui);
  --font-brand:   var(--ag-font-ui);

  /* ── Brand colors ─────────────────────────────────────────── */
  --color-solar:        var(--ag-solar, #E98245);
  --color-solar-hover:  #D97338;
  --color-solar-press:  #C9652F;
  --color-solar-deep:   #9A2B00;
  --color-fire:         var(--ag-fire, #E98245);
  --color-fire-start:   #FF7020;   /* logo gradient start */
  --color-fire-end:     #FFD1A0;   /* logo gradient end */
  --color-fire-gradient: linear-gradient(135deg, var(--color-fire-start) 0%, var(--color-fire-end) 100%);
  --gradient-cta: linear-gradient(90deg, var(--ag-cta-start, #F45122) 0%, var(--ag-cta-end, #FF955D) 100%);
  --gradient-cta-hover: linear-gradient(90deg, var(--ag-cta-hover-start, #E94A1D) 0%, var(--ag-cta-hover-end, #FF864B) 100%);
  --gradient-cta-pressed: linear-gradient(90deg, var(--ag-cta-pressed-start, #D94318) 0%, var(--ag-cta-pressed-end, #F4773E) 100%);

  --color-obsidian:     var(--ag-text-primary, #00121D);   /* primary text + dark CTAs */
  --color-charcoal:     var(--ag-text-primary, #00121D);   /* legacy alias — points to text-primary */
  --color-charcoal-alt: #0F1419;                            /* design-system alt near-black */
  --color-cream:        #F9F7F4;
  --color-beige:        #F1DAC3;
  --color-sand:         #E8E6E1;
  --color-white:        var(--ag-canvas, #FFFFFF);
  --color-paper:        var(--ag-canvas, #FFFFFF);

  /* ── Semantic surfaces & text ─────────────────────────────── */
  --canvas:             var(--ag-canvas, #FFFFFF);
  --surface:            var(--ag-surface, #FFFFFF);
  --surface-elevated:   var(--ag-surface-elevated, #FFFFFF);
  --surface-warm:       var(--ag-surface-warm, #FAFAF8);
  --background-alt:     var(--ag-bg-alt, #F1DAC3);
  --background-soft:    var(--ag-bg-soft, #F4F7F6);

  --fg-1: var(--ag-text-primary, #00121D);
  --fg-2: var(--ag-text-secondary, #4A453F);
  --fg-3: var(--ag-text-muted, #8A877F);
  --fg-on-fire: #FFFFFF;

  --border:         var(--ag-border, rgba(0, 18, 29, 0.06));
  --border-subtle:  var(--ag-border-subtle, rgba(0, 18, 29, 0.03));
  --border-strong:  var(--ag-border-strong, rgba(0, 18, 29, 0.15));

  --color-error:    var(--ag-error, #E53E3E);
  --color-success:  var(--ag-success, #38A169);
  --color-warning:  var(--ag-warning, #D69E2E);
  --color-info:     var(--ag-info, #3182CE);
  --color-online:   var(--ag-online, #22C55E);
  --color-star:     var(--ag-star, #FBBF24);
  --color-link:     var(--ag-link, #3B82F6);

  /* ── Gradients ────────────────────────────────────────────── */
  --gradient-fire:      linear-gradient(135deg, #FF7020 0%, #FFD1A0 100%);
  --gradient-solar:     linear-gradient(135deg, #E98245 0%, #1E3A5F 100%);
  --gradient-grounded:  linear-gradient(160deg, #00121D 0%, #1E3A5F 100%);
  --gradient-agape:     linear-gradient(160deg, #00121D 0%, #C2651F 50%, #E98245 100%);
  --gradient-warm:      linear-gradient(135deg, #FFFFFF 0%, #F9F7F4 30%, #FF7A3D 100%);
  --overlay-card:       linear-gradient(to top, rgba(0,10,18,0.92) 0%, rgba(0,10,18,0.5) 50%, transparent 100%);
  --overlay-protect:    linear-gradient(to bottom, rgba(0,0,0,0) 40%, rgba(0,0,0,0.4) 100%);

  /* ── Spacing (4px base) ───────────────────────────────────── */
  --space-xs:   4px;
  --space-sm:   8px;
  --space-md:   12px;
  --space-base: 16px;
  --space-lg:   24px;
  --space-xl:   32px;
  --space-2xl:  48px;
  --space-3xl:  64px;
  --space-4xl:  96px;

  /* ── Radius ───────────────────────────────────────────────── */
  --radius-xs:    4px;
  --radius-sm:    8px;     /* buttons */
  --radius-md:    12px;    /* inputs */
  --radius-lg:    16px;    /* small cards */
  --radius-card:  20px;    /* standard card */
  --radius-xl:    24px;    /* large cards, banners */
  --radius-2xl:   32px;
  --radius-huge:  40px;    /* hero card / Card.tsx default */
  --radius-pill:  100px;   /* CTAs, badges */
  --radius-full:  9999px;

  /* ── Shadow / Elevation — warm-brown tinted, not gray ──────── */
  --shadow-subtle:        0 1px 4px rgba(0, 0, 0, 0.03);
  --shadow-card:          0 4px 20px rgba(0, 18, 29, 0.06);
  --shadow-card-hover:    0 8px 30px rgba(0, 18, 29, 0.10);
  --shadow-card-elevated: 0 20px 60px rgba(107, 94, 85, 0.08);
  --shadow-luxury:        0 40px 80px -20px rgba(107, 94, 85, 0.08);
  --shadow-glass:         0 8px 32px rgba(0, 18, 29, 0.04);
  --shadow-cta:           0 10px 30px -5px rgba(233, 130, 69, 0.40);
  --shadow-cta-soft:      0 6px 24px rgba(237, 116, 61, 0.36);
  --shadow-orange-glow:   0 0 8px rgba(255, 112, 32, 0.15);
  --shadow-elite:         0 20px 50px rgba(0, 0, 0, 0.04);   /* legacy alias */

  /* ── Motion ───────────────────────────────────────────────── */
  --ease-out:    cubic-bezier(0.16, 1, 0.3, 1);     /* primary button */
  --ease-spring: cubic-bezier(0.2, 0.8, 0.2, 1);    /* card lifts */
  --ease-smooth: cubic-bezier(0.4, 0, 0.2, 1);      /* nav, hover */
  --dur-fast:    0.2s;
  --dur-base:    0.3s;
  --dur-slow:    0.5s;

  /* ── Layout ──────────────────────────────────────────────── */
  --header-height: 90px;
  --header-height-scrolled: 80px;
  --content-max: 1440px;
}

* {
  box-sizing: border-box;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  outline: none !important;
}

*:focus {
  outline: none !important;
}

html,
body {
  /* CORE-639 — base colour under the global background layer (§1 --bg-base).
     The layered gradient/glows live on body::before below. */
  background-color: var(--bg-base, #FCF5E7);
  color: var(--color-charcoal);
  margin: 0;
  padding: 0;
  min-height: 100%;
  font-family: var(--ag-font-ui);
  font-synthesis: none;
  overflow-x: hidden;
}

html[data-theme="dark"],
html[data-theme="dark"] body {
  background-color: var(--bg-base, #202226);
}

/* Transitional guard for older RN-Web call sites that still emit a literal
   legacy family inline. Inline styles win normal inheritance, so the global
   migration must neutralise them until each native-shared component can be
   migrated without changing iOS/Android typography. */
html [style*="font-family: Raleway"],
html [style*="font-family: Inter"],
html [style*="font-family: 'Inter'"],
html [style*='font-family: "Inter"'],
html [style*="font-family: -apple-system"],
html [style*="font-family: system-ui"],
html [style*="font-family: 'DM Sans'"],
html [style*='font-family: "DM Sans"'] {
  font-family: var(--ag-font-ui) !important;
  font-synthesis: none !important;
}

/* CORE-1118 — RN-Web's own `font: 14px System` base rule for <Text>/<TextInput>
   is rewritten at bootstrap in src/styles/webTextDefaultFont.ts. It cannot be
   overridden from here: its class names are `css-<hash>` in production (named
   `css-text…` only in dev), and no selector can sit strictly between that
   base class and RN-Web's atomic fontFamily class. */

/* --- THE VIGNETTE NUKE --- */
/* This removes the persistent blue glow identified as #preact-border-shadow-host */
#preact-border-shadow-host,
[id*="preact-border-shadow"],
.animate-breathing {
  display: none !important;
  visibility: hidden !important;
  opacity: 0 !important;
  pointer-events: none !important;
  z-index: -9999 !important;
}

/* --- TYPOGRAPHY: BOLD & GROUNDED --- */

h1,
.h1-supreme {
  font-family: var(--ag-font-brand);
  font-weight: 500;
  font-size: 56px;
  line-height: 64px;
  letter-spacing: -0.01em;
  color: var(--color-charcoal);
  margin: 0;
}

.subtitle-elite {
  font-family: var(--ag-font-ui);
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: var(--color-fire-start);
  margin-bottom: 32px;
  display: block;
}

.body-large {
  font-family: var(--ag-font-ui);
  font-size: 18px;
  line-height: 28px;
  color: #5C5652;
  font-weight: 400;
  max-width: 520px;
}

/* --- THE CRYSTAL HEADER --- */

.header-noble {
  /* CORE-639 — the frosted header backing is set INLINE in Header.tsx
     (themeColors.headerBg) so it's immune to the cached /global.css that kept
     resetting header styles. This class no longer forces a background; it clears the border/shadow (divider is a real <View>,
     styles.headerDivider). */
  border-bottom: none !important;
  box-shadow: none !important;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1) !important;
}

.header-blur {
  /* CORE-639 — background set inline (see .header-noble). CORE-1894 — no backdrop-filter: the header is opaque. */
  border-bottom: none !important;
  box-shadow: none !important;
}

/* --- COMPONENTS: THE FIRE ACCENT --- */

.convex-fire {
  background: var(--color-fire) !important;
  color: #FFFFFF !important;
  border-radius: 100px !important;
  box-shadow: 0 10px 30px rgba(233, 130, 69, 0.25) !important;
  transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1) !important;
  font-weight: 700 !important;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0 !important;
  text-transform: none;
  border: none !important;
}

.convex-fire:hover {
  transform: translateY(-2px);
  filter: brightness(1.1);
  box-shadow: 0 15px 40px rgba(233, 130, 69, 0.35) !important;
}

/* --- MINIMALIST RIBBON ACTION --- */

.ribbon-link {
  color: var(--color-fire) !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  text-transform: none;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  display: flex !important;
  align-items: center !important;
  gap: 12px;
  transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1) !important;
}

.ribbon-link:hover {
  letter-spacing: 0 !important;
  transform: translateX(4px);
}

.convex-light {
  background: rgba(255, 253, 249, 0.90) !important;
  backdrop-filter: blur(30px) !important;
  -webkit-backdrop-filter: blur(30px) !important;
  border-radius: 100px !important;
  border: 1px solid rgba(214, 153, 113, 0.24) !important;
  box-shadow: 0 14px 40px rgba(111, 78, 55, 0.075), 0 0 26px rgba(205, 157, 122, 0.045) !important;
  transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1) !important;
}

.convex-light:hover {
  background: var(--color-white) !important;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.04) !important;
  transform: translateY(-2px) !important;
}

/* --- THE CRYSTAL GLASS SYSTEM --- */

.convex-glass-fire {
  background: rgba(233, 130, 69, 0.1) !important;
  backdrop-filter: blur(25px) saturate(180%) !important;
  -webkit-backdrop-filter: blur(25px) saturate(180%) !important;
  border: 1px solid rgba(233, 130, 69, 0.3) !important;
  box-shadow: 0 20px 80px rgba(233, 130, 69, 0.1) !important;
}

.convex-glass-light {
  background: rgba(255, 253, 249, 0.82) !important;
  backdrop-filter: blur(30px) saturate(150%) !important;
  -webkit-backdrop-filter: blur(30px) saturate(150%) !important;
  border: 1px solid rgba(214, 153, 113, 0.24) !important;
  box-shadow: 0 14px 40px rgba(111, 78, 55, 0.075), 0 0 26px rgba(205, 157, 122, 0.045) !important;
}

/* --- TICKERS ENGINE --- */

.ticker-container {
  overflow: hidden;
  white-space: nowrap;
  width: 100%;
}

.ticker-col {
  overflow: hidden;
  height: 100%;
  flex: 1;
  position: relative;
  padding-bottom: 0;
  mask-image: linear-gradient(to bottom, transparent 0, black 6px, black calc(100% - 6px), transparent 100%);
  -webkit-mask-image: linear-gradient(to bottom, transparent 0, black 6px, black calc(100% - 6px), transparent 100%);
}

.ticker-container-v {
  overflow: hidden;
  height: 800px;
  width: 100%;
  position: relative;
  /* Minimalist, sharp fade 'zasvet' - only at extreme edges */
  mask-image: linear-gradient(to bottom, transparent 0, black 6px, black calc(100% - 6px), transparent 100%);
  -webkit-mask-image: linear-gradient(to bottom, transparent 0, black 6px, black calc(100% - 6px), transparent 100%);
}

.ticker-track {
  display: inline-flex;
  animation: ticker-h 50s linear infinite;
  gap: 16px;
}

.ticker-track-v {
  display: flex;
  flex-direction: column;
  animation: ticker-v 40s linear infinite;
  /* FASTER ANIMATION */
  will-change: transform;
}

@keyframes ticker-h {
  0% {
    transform: translateX(0);
  }

  100% {
    transform: translateX(-50%);
  }
}

@keyframes ticker-v {
  0% {
    transform: translateY(0);
  }

  100% {
    transform: translateY(-50%);
    /* Corrected for [...masterGrid, ...masterGrid] loop */
  }
}

.ribbon-separator {
  width: 100%;
  height: 48px;
  /* REDUCED SPACE BETWEEN MASTERS */
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.ribbon-separator::before {
  content: '';
  width: 1px;
  height: 40px;
  background: rgba(24, 20, 17, 0.08);
}

.ribbon-separator::after {
  content: '';
  position: absolute;
  width: 6px;
  height: 6px;
  border-radius: 3px;
  background: var(--color-fire-start);
  box-shadow: 0 0 10px rgba(233, 130, 69, 0.4);
}

/* --- CARDS & PLATES --- */

.master-elite-card {
  background: #FFFFFF;
  border-radius: 40px;
  border: 1px solid rgba(0, 0, 0, 0.03);
  box-shadow: 0 20px 80px rgba(0, 0, 0, 0.04);
  transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.master-elite-card:hover {
  transform: translateY(-8px) scale(1.01);
  box-shadow: 0 40px 100px rgba(0, 0, 0, 0.08);
}

/* --- PROGRESSIVE ONBOARDING --- */
.onboarding-step-enter {
  animation: slide-up-fade 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes slide-up-fade {
  0% {
    transform: translateY(20px);
    opacity: 0;
  }

  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

.onboarding-pill {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.onboarding-pill:hover {
  border-color: var(--color-fire);
  background: rgba(233, 130, 69, 0.03);
}

.glass-inset {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 100px;
}

.category-tag {
  padding: 14px 32px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 99px;
  color: var(--color-charcoal);
  font-family: var(--ag-font-ui);
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);
}

/* --- ANIMATIONS --- */

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 0.6;
  }

  50% {
    transform: scale(2.2);
    opacity: 0;
  }

  100% {
    transform: scale(1);
    opacity: 0.6;
  }
}

.pulse-animation {
  animation: pulse 3s ease-in-out infinite;
}

/* --- HIGHLIGHT MODERN --- */
.highlight-modern {
  color: #E98245;
  position: relative;
}

.highlight-modern::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: rgba(233, 130, 69, 0.15);
  border-radius: 2px;
}

/* ============================================ */
/* DARK MODE — "THE OBSIDIAN SANCTUARY"         */
/* ============================================ */

/* System auto-detect (flash prevention before JS loads) */
@media (prefers-color-scheme: dark) {
  html:not([data-theme="light"]) {
    --color-fire: var(--ag-fire, #E98245);
    --color-fire-start: #FF7020;
    --color-fire-end: #CC8040;
    --color-obsidian: var(--ag-text-primary, #F5F0EB);
    --color-charcoal: var(--ag-text-primary, #F5F0EB);
    --color-charcoal-alt: #2A2D33;
    --color-white: var(--ag-canvas, #202226);
    --color-paper: var(--ag-canvas, #202226);
    --shadow-subtle: 0 2px 10px rgba(0, 0, 0, 0.3);
    --shadow-card: 0 4px 20px rgba(0, 0, 0, 0.3);
    --shadow-card-hover: 0 8px 30px rgba(0, 0, 0, 0.4);
    --shadow-card-elevated: 0 20px 60px rgba(0, 0, 0, 0.4);
    --shadow-elite: 0 20px 50px rgba(0, 0, 0, 0.4);
    color-scheme: dark;
  }

  html:not([data-theme="light"]),
  html:not([data-theme="light"]) body {
    /* CORE-639 — base under the global bg layer (was forced #17181C). */
    background-color: var(--bg-base, #202226) !important;
    color: #F5F0EB !important;
  }
}

/* Explicit dark mode (JS-controlled via data-theme attribute) */
[data-theme="dark"] {
  --color-fire: var(--ag-fire, #E98245);
  --color-fire-start: #FF7020;
  --color-fire-end: #CC8040;
  --color-obsidian: var(--ag-text-primary, #F5F0EB);
  --color-charcoal: var(--ag-text-primary, #F5F0EB);
  --color-charcoal-alt: #2A2D33;
  --color-white: var(--ag-canvas, #202226);
  --color-paper: var(--ag-canvas, #202226);
  --shadow-subtle: 0 2px 10px rgba(0, 0, 0, 0.3);
  --shadow-card: 0 4px 20px rgba(0, 0, 0, 0.3);
  --shadow-card-hover: 0 8px 30px rgba(0, 0, 0, 0.4);
  --shadow-card-elevated: 0 20px 60px rgba(0, 0, 0, 0.4);
  --shadow-elite: 0 20px 50px rgba(0, 0, 0, 0.4);
  color-scheme: dark;
}

[data-theme="dark"],
[data-theme="dark"] body {
  /* CORE-639 — base under the global bg layer (was forced #17181C). */
  background-color: var(--bg-base, #202226) !important;
  color: #F5F0EB !important;
}

/* --- DARK: Header (CORE-639 — backing set inline; CORE-1894 — no backdrop-filter) --- */
[data-theme="dark"] .header-blur {
  border-bottom: none !important;
  box-shadow: none !important;
}

/* --- DARK: Glass System --- */
[data-theme="dark"] .convex-glass-light {
  background: rgba(40, 39, 37, 0.84) !important;
  backdrop-filter: blur(30px) saturate(150%) !important;
  -webkit-backdrop-filter: blur(30px) saturate(150%) !important;
  border: 1px solid rgba(241, 181, 142, 0.18) !important;
  box-shadow: 0 16px 46px rgba(0, 0, 0, 0.22), 0 0 28px rgba(126, 91, 68, 0.05) !important;
}

[data-theme="dark"] .convex-glass-fire {
  background: rgba(233, 130, 69, 0.12) !important;
  backdrop-filter: blur(25px) saturate(180%) !important;
  -webkit-backdrop-filter: blur(25px) saturate(180%) !important;
  border: 1px solid rgba(233, 130, 69, 0.2) !important;
  box-shadow: 0 20px 80px rgba(233, 130, 69, 0.06) !important;
}

[data-theme="dark"] .convex-light {
  background: rgba(40, 39, 37, 0.90) !important;
  border: 1px solid rgba(241, 181, 142, 0.18) !important;
  box-shadow: 0 16px 46px rgba(0, 0, 0, 0.22), 0 0 28px rgba(126, 91, 68, 0.05) !important;
}

[data-theme="dark"] .convex-light:hover {
  background: rgba(40, 40, 40, 1) !important;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.3) !important;
}

[data-theme="dark"] .convex-fire {
  box-shadow: 0 10px 30px rgba(233, 130, 69, 0.2) !important;
}

[data-theme="dark"] .convex-fire:hover {
  box-shadow: 0 15px 40px rgba(233, 130, 69, 0.3) !important;
}

/* --- DARK: Cards --- */
[data-theme="dark"] .master-elite-card {
  background: #1A1A1A !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  box-shadow: 0 20px 80px rgba(0, 0, 0, 0.3) !important;
}

[data-theme="dark"] .master-elite-card:hover {
  box-shadow: 0 40px 100px rgba(0, 0, 0, 0.5) !important;
}

/* --- DARK: Typography --- */
[data-theme="dark"] h1,
[data-theme="dark"] .h1-supreme {
  color: #F5F0EB !important;
}

[data-theme="dark"] .subtitle-elite {
  color: #E98245 !important;
}

[data-theme="dark"] .body-large {
  color: #A09C98 !important;
}

[data-theme="dark"] .highlight-modern {
  color: #E98245;
}

[data-theme="dark"] .highlight-modern::after {
  background: rgba(233, 130, 69, 0.2);
}

/* --- DARK: Components --- */
[data-theme="dark"] .category-tag {
  background: #1A1A1A !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  color: #F5F0EB !important;
}

[data-theme="dark"] .ribbon-separator::before {
  background: rgba(255, 255, 255, 0.08) !important;
}

[data-theme="dark"] .ribbon-separator::after {
  background: #E98245 !important;
  box-shadow: 0 0 10px rgba(233, 130, 69, 0.4) !important;
}

[data-theme="dark"] .onboarding-pill:hover {
  border-color: #E98245;
  background: rgba(233, 130, 69, 0.06);
}

[data-theme="dark"] .glass-inset {
  background: rgba(255, 255, 255, 0.06) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
}

[data-theme="dark"] ::selection {
  background-color: rgba(233, 130, 69, 0.3) !important;
  color: #E98245 !important;
}

/* Favorite star on cards — hidden by default, shown on hover or when active */
.favorite-star {
  opacity: 0;
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.favorite-star.favorite-star-active {
  opacity: 1;
}

.convex-glass-light:hover .favorite-star {
  opacity: 1;
}

/* ─── Practice Feed Card — Premium Hover ─── */
.practice-feed-card {
  transition: transform 0.32s cubic-bezier(0.25, 0.46, 0.45, 0.94),
    box-shadow 0.32s cubic-bezier(0.25, 0.46, 0.45, 0.94),
    border-color 0.32s ease !important;
  cursor: pointer;
}

.practice-feed-card:hover {
  transform: translateY(-5px) !important;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.06),
    0 4px 12px rgba(0, 0, 0, 0.03) !important;
  border-color: rgba(0, 0, 0, 0.08) !important;
}

[data-theme="dark"] .practice-feed-card:hover {
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3),
    0 4px 12px rgba(0, 0, 0, 0.15) !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

/* ─── Live Room Card — Cinematic Hover ─── */
.live-room-card {
  transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1),
    box-shadow 0.35s ease !important;
  cursor: pointer;
}

.live-room-card:hover {
  transform: translateY(-4px) scale(1.02) !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15) !important;
}

[data-theme="dark"] .live-room-card:hover {
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4) !important;
}

/* ─── Interest Pill — Subtle Hover ─── */
.interest-pill {
  transition: all 0.22s cubic-bezier(0.4, 0, 0.2, 1) !important;
  cursor: pointer;
}

.interest-pill:hover {
  border-color: rgba(0, 0, 0, 0.15) !important;
  background-color: #F3F4F6 !important;
}

[data-theme="dark"] .interest-pill:hover {
  border-color: rgba(255, 255, 255, 0.12) !important;
  background-color: rgba(255, 255, 255, 0.06) !important;
}

/* ─── Live Pulse Animation ─── */
@keyframes live-pulse {

  0%,
  100% {
    opacity: 1;
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.18);
  }

  50% {
    opacity: 0.68;
    transform: scale(1.16);
    box-shadow: 0 0 0 5px rgba(34, 197, 94, 0);
  }
}

/* ─── Speaking Pulse Animation (practice room tiles) ─── */
@keyframes speakingPulse {
  0%, 100% { box-shadow: 0 0 12px rgba(34, 197, 94, 0.4), 0 0 4px rgba(34, 197, 94, 0.2); }
  50% { box-shadow: 0 0 20px rgba(34, 197, 94, 0.6), 0 0 8px rgba(34, 197, 94, 0.3); }
}

/* Video subtitle (closed captions) styling */
video::cue {
  background: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 16px;
  font-family: var(--ag-font-ui);
  line-height: 24px;
  padding: 4px 8px;
  border-radius: 4px;
}

/* ═══ HERO COLUMN ALIGNMENT — Single Source of Truth ═══ */

/* LEFT COLUMN: The authority. Sets the height for the entire hero row. */
.hero-left-col {
  min-height: calc(100vh - 140px);
  height: auto;
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  overflow: visible;
}

/* RIGHT COLUMN: Stretches to match left col via parent alignItems:stretch */
.hero-right-col {
  display: flex !important;
  flex-direction: column !important;
  flex: 1 !important;
  position: relative !important;
  border-radius: 20px;
  overflow: hidden !important;
  margin-top: 24px;
  align-self: stretch !important;
  height: auto !important;
}

/* Nested containers: bypass flex-height bugs on web */
.hero-visual-stack {
  position: absolute !important;
  top: 0; left: 0; right: 0; bottom: 0;
  display: flex !important;
  flex-direction: row !important;
  overflow: hidden !important;
  gap: 6px;
}

.hero-ticker-layout {
  display: flex !important;
  flex-direction: row !important;
  flex: 1 !important;
  height: 100% !important;
  overflow: hidden !important;
  gap: 6px;
}

.ticker-track-col {
  display: flex;
  flex-direction: column;
  gap: 6px;
  animation: ticker-v 40s linear infinite;
  will-change: transform;
}

.ticker-track-col--reverse {
  animation: ticker-v-reverse 55s linear infinite;
  transform: translateY(-50%);
}

@keyframes ticker-v-reverse {
  0% { transform: translateY(-50%); }
  100% { transform: translateY(0); }
}

/* ═══ HERO RESPONSIVE ═══ */
@media (max-width: 1099px) {
  .hero-left-col {
    min-height: auto;
    height: auto;
    margin-top: 0;
  }
  .hero-right-col {
    height: 500px !important;
    min-height: 500px !important;
    position: relative !important;
    margin-top: 24px;
  }
}

/* ═══ INVITE BUTTON GLOW ═══ */
.invite-btn-glow {
  transition: box-shadow 0.3s ease, transform 0.3s ease, border-color 0.3s ease !important;
}

.invite-btn-glow:hover {
  box-shadow: 0 0 20px rgba(233, 130, 69, 0.4), 0 0 40px rgba(233, 130, 69, 0.15) !important;
  transform: translateY(-2px) !important;
  border-color: #FF8844 !important;
}

.invite-btn-glow:active {
  transform: translateY(0) !important;
  box-shadow: 0 0 12px rgba(233, 130, 69, 0.3) !important;
}

/* ═══ DRAG SCROLL (no scrollbar, grab to drag) ═══ */
.drag-scroll {
  overflow-x: auto !important;
  scrollbar-width: none;
  cursor: grab;
  -webkit-user-select: none;
  user-select: none;
}

.drag-scroll::-webkit-scrollbar {
  display: none;
}

.drag-scroll:active {
  cursor: grabbing;
}

/* ═══ EVENTS CAROUSEL SCROLLBAR ═══ */
.events-carousel-scroll {
  overflow-x: auto !important;
  scrollbar-width: auto;
  scrollbar-color: rgba(0, 0, 0, 0.2) rgba(0, 0, 0, 0.06);
  padding-bottom: 16px !important;
}

.events-carousel-scroll:hover {
  scrollbar-color: #E98245 rgba(0, 0, 0, 0.06);
}

.events-carousel-scroll::-webkit-scrollbar {
  height: 8px;
}

.events-carousel-scroll::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  margin: 0 20px;
}

.events-carousel-scroll::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

.events-carousel-scroll:hover::-webkit-scrollbar-thumb {
  background: #E98245;
}

[data-theme="dark"] .events-carousel-scroll {
  scrollbar-color: rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.06);
}

[data-theme="dark"] .events-carousel-scroll:hover {
  scrollbar-color: #E98245 rgba(255, 255, 255, 0.06);
}

[data-theme="dark"] .events-carousel-scroll::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.06);
}

[data-theme="dark"] .events-carousel-scroll::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
}

[data-theme="dark"] .events-carousel-scroll:hover::-webkit-scrollbar-thumb {
  background: #E98245;
}

/* ═══ VISIBLE-SCROLLBAR SCROLLVIEW ═══
   Generic opt-in: any RN ScrollView with `dataSet={{ 'visible-scrollbar': 'true' }}`
   gets a thin, always-visible scrollbar on web. macOS hides overlay scrollbars by
   default, which left CreateRoomModal users not realising there was more content
   below the «Дополнительно» accordion once expanded. */
[data-visible-scrollbar="true"] {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.25) transparent;
}

[data-visible-scrollbar="true"]::-webkit-scrollbar {
  width: 10px;
}

[data-visible-scrollbar="true"]::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.04);
  border-radius: 5px;
}

[data-visible-scrollbar="true"]::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.25);
  border-radius: 5px;
}

[data-visible-scrollbar="true"]:hover::-webkit-scrollbar-thumb {
  background: #E98245;
}

[data-theme="dark"] [data-visible-scrollbar="true"] {
  scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
}

[data-theme="dark"] [data-visible-scrollbar="true"]::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.06);
}

[data-theme="dark"] [data-visible-scrollbar="true"]::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
}

[data-theme="dark"] [data-visible-scrollbar="true"]:hover::-webkit-scrollbar-thumb {
  background: #E98245;
}

/* ═══ CIRCLES PREMIUM ANIMATION ═══ */
.circle-card-premium {
  transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1) !important;
  position: relative;
  z-index: 1;
}

.circle-card-premium:hover {
  transform: scale(1.15) translateY(-8px);
  z-index: 50 !important;
}

.circle-card-premium .circle-image-container {
  transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1) !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.circle-card-premium:hover .circle-image-container {
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25), 0 0 40px rgba(233, 130, 69, 0.4);
  filter: brightness(1.05);
  border-width: 4px !important;
}

.circle-card-premium .circle-image-img {
  transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1) !important;
}

.circle-card-premium:hover .circle-image-img {
  transform: scale(1.08);
}

/* ═══ SNAP CAROUSEL — Unified Horizontal Scroll ═══ */
.snap-carousel {
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.snap-carousel::-webkit-scrollbar {
  display: none;
}

.snap-carousel > * {
  scroll-snap-align: start;
}

/* ════════════════════════════════════════════════════════════
   Agapella Design System — Semantic Classes
   (added in refactor/design-system-tier4; canonical from
   docs/agapella-design-system/project/colors_and_type.css)
   ════════════════════════════════════════════════════════════ */

/* Small semantic label above section titles. */
.overline {
  font-family: var(--ag-font-ui);
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0;
  text-transform: none;
  font-weight: 600;
  color: var(--ag-accent-text); /* CORE-1733 — акцентный текст */
}

/* .bullet-agape — Agapella's branded "✦" four-pointed star bullet.
   Replaces bog-standard "•" in chat copy and lists. NEVER substitute
   with 🌟 / ✨ / emoji — this is a brand glyph, not an emoji. */
.bullet-agape::before {
  content: "✦";
  color: var(--ag-accent-text); /* CORE-1733 — акцентный текст */
  margin-right: 0.5em;
  font-weight: 400;
}

/* .ag-star — inline ✦ glyph for use inside text (no margin) */
.ag-star {
  color: var(--ag-accent-text); /* CORE-1733 — акцентный текст */
}

/* Semantic raw-heading fallback for web-only content. */
h1, .h1-supreme, .h1-display {
  font-family: var(--ag-font-brand);
  font-size: 56px;
  line-height: 64px;
  font-weight: 500;
  letter-spacing: -0.01em;
}

h2, .h2-display {
  font-family: var(--ag-font-brand);
  font-size: 40px;
  line-height: 48px;
  font-weight: 500;
  letter-spacing: -0.01em;
}

h3, h4, h5, .h3-display {
  font-family: var(--ag-font-ui);
  font-size: 22px;
  line-height: 28px;
  font-weight: 600;
  letter-spacing: 0;
}

.body-large, .caption, .micro, .button-text {
  font-family: var(--ag-font-ui);
}

.micro {
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0;
  font-weight: 400;
  color: var(--fg-3);
}

.button-text {
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0;
  font-weight: 700;
  text-transform: none;
}

/* ============================================================
   <input> chrome reset — RN-Web rendering hygiene.

   1. Browser-injected affordances (native «show password», clear-
      input, etc.) are hidden — Chrome / Edge / Yandex inject them
      automatically and they overlap our custom eye icon inside
      `Input.tsx`. Safari iOS doesn't inject anything (which is why
      the bug only reproes on Chromium-family).

   2. Default <input> chrome (background, border, border-radius,
      focus ring) is also reset. RN-Web maps `<TextInput>` to a
      raw <input>, and on dark mobile the browser's default styling
      reads as a *second* bordered box drawn INSIDE our wrapper —
      so the wrapper's right-side icon (eye) appears OUTSIDE that
      inner box and looks misplaced. With the reset, the wrapper
      is the only visible bordered surface; left/right icons sit
      naturally inside the padded area.
   ============================================================ */
input::-ms-reveal,
input::-ms-clear,
input::-webkit-credentials-auto-fill-button,
input::-webkit-textfield-decoration-container,
input::-webkit-search-cancel-button,
input::-webkit-search-decoration {
  visibility: hidden !important;
  display: none !important;
  pointer-events: none !important;
  -webkit-appearance: none !important;
  appearance: none !important;
}

input[type="text"],
input[type="email"],
input[type="password"],
input[type="search"],
input[type="tel"],
input[type="url"],
input[type="number"] {
  background-color: transparent !important;
  border: none !important;
  outline: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  -webkit-appearance: none !important;
  appearance: none !important;
}

input[type="text"]:focus,
input[type="email"]:focus,
input[type="password"]:focus,
input[type="search"]:focus,
input[type="tel"]:focus,
input[type="url"]:focus,
input[type="number"]:focus {
  outline: none !important;
  box-shadow: none !important;
}

/* ============================================================
   CORE-639 — Global background + logo, semantic tokens (§1)

   Executable copy of the spec's token table. VALUES ARE MANDATORY
   and must match CORE-639 §1 exactly — do not substitute a «similar»
   colour and do not flatten rgba() to opaque hex (the transparency is
   part of the visual solution). Light lives on :root, Dark overrides
   on html[data-theme="dark"] (JS sets data-theme before React mounts,
   see App.tsx) with a prefers-color-scheme fallback so system-dark is
   correct even in the pre-hydration window (no FOUC).

   This block only DEFINES the tokens (Phase A). Applying them to the
   global background layer, surfaces and the theme-aware logo lands in
   the following CORE-639 phases; nothing reads these vars yet, so this
   change is visually inert.
   ============================================================ */
:root {
  /* Background — CORE-1733: общий фон платформы v4 по статье Confluence 110756133
     «Agapella Web — общие фоны и поверхности» (светлая v4 от 16.10.2026). Слои и
     стопы — дословно из «Единого CSS» статьи; первый слой рисуется поверх
     остальных. Без виньетки: статья запрещает тёмную виньетку в Light. */
  --bg-base: #FCF5E7;
  --bg-page-image:
    radial-gradient(ellipse at 47% 28%, #FFFCF4 0%, rgba(255, 252, 244, 0.72) 28%, rgba(255, 252, 244, 0) 68%),
    radial-gradient(ellipse at 0% 55%, rgba(221, 190, 145, 0.16) 0%, rgba(221, 190, 145, 0) 60%),
    radial-gradient(ellipse at 100% 100%, rgba(231, 200, 156, 0.12) 0%, rgba(231, 200, 156, 0) 58%),
    linear-gradient(125deg, #F5E8D1 0%, #FCF5E7 44%, #F6EBD8 100%);

  /* Surfaces */
  --surface-primary:   rgba(255, 255, 255, 0.78);  /* large cards / panels */
  --surface-secondary: rgba(255, 255, 255, 0.58);  /* nested / secondary */
  --surface-hover:     rgba(255, 255, 255, 0.90);  /* hover */
  --surface-active:    rgba(255, 249, 242, 0.96);  /* active / selected */
  --header-bg:         rgba(251, 250, 248, 0.78);  /* header backing */

  /* Borders */
  --border-subtle: rgba(43, 39, 48, 0.12);  /* default */
  --border-strong: rgba(43, 39, 48, 0.20);  /* hover / selected */

  /* Text */
  --text-primary:   #2B2730;
  --text-secondary: #625B67;
  /* CORE-1733 — = ThemeContext textMuted: #847D88 давал 3.3–3.7:1 на v4. */
  --text-muted:     #686170;

  /* Accents */
  --accent-orange:        #E98245;
  --accent-orange-hover:  #D97338;
  --accent-orange-active: #C9652F;
  --accent-yellow:        #F4C453;
  /* CORE-1733 — оранжевый как цвет текста и значимого глифа (src/styles/accentText.ts):
     #E98245 на v4 — 2.2–2.5:1; #9A421F из статьи 110756133 — 5.0–6.6:1. */
  --ag-accent-text:       #9A421F;

  /* Focus + elevation. CORE-1733 — кольцо фокуса сплошное: полупрозрачный
     оранжевый .36 на сливочной странице давал ~1.5:1 и не читался. */
  --focus-ring:     #9A421F;
  --shadow-surface: 0 18px 52px rgba(82, 62, 47, 0.09);
}

html[data-theme="dark"] {
  /* CORE-1733 — тёмный фон статьи 110756133: непрозрачная основа и два
     низкоинтенсивных radial (те же слои, что liveTokens.dark.pageBgImage). */
  --bg-base: #202226;
  --bg-page-image:
    radial-gradient(ellipse at 4% 60%, rgba(233, 130, 69, 0.065), transparent 53%),
    radial-gradient(ellipse at 94% 8%, rgba(215, 183, 164, 0.045), transparent 56%);

  --surface-primary:   rgba(40, 39, 37, 0.84);
  --surface-secondary: rgba(44, 43, 40, 0.68);
  --surface-hover:     rgba(50, 48, 44, 0.90);
  --surface-active:    rgba(58, 53, 47, 0.94);
  --header-bg:         #35424B;

  --border-subtle: rgba(243, 236, 227, 0.14);
  --border-strong: rgba(243, 236, 227, 0.24);

  --text-primary:   #F3ECE3;
  --text-secondary: #C7BEC5;
  /* CORE-1733 — = ThemeContext textMuted: прежний #9C949D давал 4.38–4.49:1 на поверхностях v4. */
  --text-muted:     #A8A0A9;

  --accent-orange:        #E98245;
  --accent-orange-hover:  #F09158;
  --accent-orange-active: #D97338;
  --accent-yellow:        #F4C453;
  --ag-accent-text:       #F0A06D;  /* CORE-1733 — #E98245 проседает до 4.4 на поверхностях */

  --focus-ring:     #F0A06D;
  --shadow-surface: 0 18px 56px rgba(9, 9, 8, 0.38);
}

/* System-dark before JS resolves an explicit data-theme (anti-FOUC). */
@media (prefers-color-scheme: dark) {
  html:not([data-theme="light"]) {
    --bg-base: #202226;
    --bg-page-image:
      radial-gradient(ellipse at 4% 60%, rgba(233, 130, 69, 0.065), transparent 53%),
      radial-gradient(ellipse at 94% 8%, rgba(215, 183, 164, 0.045), transparent 56%);

    --surface-primary:   rgba(40, 39, 37, 0.84);
    --surface-secondary: rgba(44, 43, 40, 0.68);
    --surface-hover:     rgba(50, 48, 44, 0.90);
    --surface-active:    rgba(58, 53, 47, 0.94);
    --header-bg:         #35424B;

    --border-subtle: rgba(243, 236, 227, 0.14);
    --border-strong: rgba(243, 236, 227, 0.24);

    --text-primary:   #F3ECE3;
    --text-secondary: #C7BEC5;
    --text-muted:     #A8A0A9;

    --accent-orange:        #E98245;
    --accent-orange-hover:  #F09158;
    --accent-orange-active: #D97338;
    --accent-yellow:        #F4C453;
    --ag-accent-text:       #F0A06D;

    --focus-ring:     #F0A06D;
    --shadow-surface: 0 18px 56px rgba(9, 9, 8, 0.38);
  }
}

/* Keep the sunlit part of the hero landscape inside narrow/tall crops. */
@media (max-width: 1119px) {
  #home-release-hero-atmosphere > div:first-child {
    background-position: 34% center !important;
  }
}

/* ============================================================
   CORE-639 — Global background layer (§2), CORE-1733 — значения v4

   One fixed decorative layer for the whole app shell — NOT copied per
   page. Sits behind all content (z-index:-1, pointer-events:none) so
   page/section containers made transparent (colors.pageBg on web) reveal
   it. Static (no animation), no background-attachment:fixed (Safari
   artefacts).

   CORE-1733 — слои берутся из токенов --bg-base / --bg-page-image выше
   (статья 110756133). Тема переключает токены, а не правило: раньше рецепт
   повторялся шесть раз (слой и подложка входа × светлая, тёмная, системная
   тёмная), и каждую правку надо было делать во всех копиях. Статья требует
   именно такой слой — неподвижный, размером с окно: радиальные градиенты не
   растягиваются по высоте длинной ленты.
   ============================================================ */
html,
body {
  min-height: 100%;
}

#root {
  min-height: 100dvh;
}

body::before {
  content: '';
  position: fixed;
  inset: 0;
  z-index: -1;              /* behind all app content */
  pointer-events: none;    /* never intercepts clicks (§8) */
  background-color: var(--bg-base);
  background-repeat: no-repeat;
  background-size: cover;
  background-image: var(--bg-page-image);
}

/* CORE-639 — auth backdrop. The web auth screens are transparentModals; with
   the shell now transparent they'd show the page behind. Owner decision: auth
   sits on the unified global background. This paints the §2 background on an
   in-flow (absolute-fill) element inside the auth screen so it covers the page
   behind and the form floats on the global gradient. Same recipe as
   body::before; layout (absolute inset:0) is set on the RN View. */
.ag-global-bg {
  background-color: var(--bg-base);
  background-repeat: no-repeat;
  background-size: cover;
  background-image: var(--bg-page-image);
}

/* ============================================================
   CORE-639 §6 — focus-visible ring. The global `*:focus{outline:none}`
   above strips the default indicator; keyboard focus must still be
   visible (WCAG). Scoped to interactive elements so design box-shadows
   on cards/panels are untouched. Uses the theme --focus-ring token.
   ============================================================ */
/* Text fields: `input`/`textarea` match :focus-visible even on mouse click, so
   a ring on every click read as an unwanted orange outline (owner request) —
   they stay without a ring for mouse and touch. CORE-1733: for the keyboard the
   caret alone is not a distinguishable focus indicator, so fields get the ring
   when focus came from the keyboard: src/utils/focusSource.ts marks
   <html data-focus-source="keyboard"> on Tab / arrows and drops it on pointer.

   CORE-1733 — the ring itself: the old 3px rgba(233,130,69,.36) measured ~1.5:1
   on the v4 cream page and was not distinguishable. Now a solid 2px ring in the
   accent ink (#9A421F light, #F0A06D dark — ≥5:1 on page, header and surfaces)
   with a 2px gap in the page colour, so it also stays visible around orange
   CTAs and on any surface. */
a:focus-visible,
button:focus-visible,
select:focus-visible,
[role="button"]:focus-visible,
[role="link"]:focus-visible,
[role="tab"]:focus-visible,
[tabindex]:focus-visible {
  outline: none !important;
  box-shadow: 0 0 0 2px var(--bg-base), 0 0 0 4px var(--focus-ring) !important;
}

/* The visible field is the wrapper around the bare <input> (see the input reset
   above), so the ring goes on the input's parent.
   CORE-1791 — черновик сообщения на телефонной ширине — редактор contentEditable
   (DraftField, CORE-1730), не <textarea>: без своего селектора Tab на нём кольца не давал. */
html[data-focus-source="keyboard"] :has(> input:focus),
html[data-focus-source="keyboard"] :has(> [contenteditable="true"]:focus),
html[data-focus-source="keyboard"] :has(> textarea:focus) {
  box-shadow: 0 0 0 2px var(--bg-base), 0 0 0 4px var(--focus-ring) !important;
}
@supports not selector(:has(*)) {
  html[data-focus-source="keyboard"] [contenteditable="true"]:focus,
  html[data-focus-source="keyboard"] input:focus,
  html[data-focus-source="keyboard"] textarea:focus {
    box-shadow: 0 0 0 2px var(--bg-base), 0 0 0 4px var(--focus-ring) !important;
  }
}
/* Practices helper: slow, asynchronous sparks travelling around the lotus.
   The artwork itself remains still; reduced-motion users see the static asset. */
@keyframes agapella-orbit-clockwise {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes agapella-orbit-counterclockwise {
  from { transform: rotate(360deg); }
  to { transform: rotate(0deg); }
}

@keyframes agapella-orbit-twinkle {
  0%, 100% { opacity: 0.35; box-shadow: 0 0 4px rgba(255, 174, 103, 0.35); }
  45% { opacity: 1; box-shadow: 0 0 11px rgba(255, 174, 103, 0.95); }
  70% { opacity: 0.62; box-shadow: 0 0 7px rgba(255, 217, 178, 0.65); }
}

.agapella-spark-layer { position: absolute; inset: 0; pointer-events: none; }
.agapella-spark-layer-compact {
  transform: scale(0.6);
  transform-origin: center;
}
.agapella-orbit {
  position: absolute;
  left: 50%;
  top: 50%;
  border-radius: 999px;
  transform-origin: center;
  will-change: transform;
}
.agapella-orbit-one { width: 136px; height: 136px; margin: -68px 0 0 -68px; }
.agapella-orbit-two { width: 112px; height: 112px; margin: -56px 0 0 -56px; }
.agapella-orbit-three { width: 156px; height: 156px; margin: -78px 0 0 -78px; }
.agapella-orbit-spark {
  position: absolute;
  top: -2px;
  left: 50%;
  width: 6px;
  height: 6px;
  margin-left: -3px;
  border-radius: 50%;
  background: #ffd0a4;
}
.agapella-orbit-spark-small {
  top: -1.5px;
  width: 4px;
  height: 4px;
  margin-left: -2px;
  background: #fff1db;
}
.agapella-orbit-one { animation: agapella-orbit-clockwise 13s linear infinite; }
.agapella-orbit-two { animation: agapella-orbit-counterclockwise 18s linear infinite; }
.agapella-orbit-three { animation: agapella-orbit-clockwise 24s linear infinite; }
.agapella-orbit-one .agapella-orbit-spark { animation: agapella-orbit-twinkle 2.8s ease-in-out infinite; }
.agapella-orbit-two .agapella-orbit-spark { animation: agapella-orbit-twinkle 3.6s ease-in-out -1.1s infinite; }
.agapella-orbit-three .agapella-orbit-spark { animation: agapella-orbit-twinkle 4.2s ease-in-out -2.3s infinite; }

@media (prefers-reduced-motion: reduce) {
  .agapella-orbit,
  .agapella-orbit-spark { animation: none !important; }
}

/* ============================================================
   CORE-974 — compatibility layer for legacy orange actions.

   The app still has older RN-web screens whose buttons are styled inline
   instead of going through Button / GradientCtaButton. Limit this bridge to
   interactive elements carrying one of the historical solid-orange values;
   badges, progress bars and decorative orange blocks are intentionally left
   untouched. New code should use the shared components and CTA tokens.

   CORE-1755 (QA 26.10.2026) — `data-cta="own"` opts an element out. The
   creation wizard's «Продолжить» draws its own compact 14px rectangle (article
   «Единая кнопка перехода · все шаги»); this bridge was turning it back into a
   capsule with the legacy gradient and shadow because its inline fallback colour
   is the CTA solid. The opt-out sits inside `:where()` so the bridge keeps its
   0,1,0 specificity and the disabled rule below still wins over its shadow.
   ============================================================ */
:where(button, [role="button"], [aria-label], [tabindex="0"]):where(:not([data-cta="own"])):is(
  [style*="background-color: #E98245" i],
  [style*="background-color: #E9834B" i],
  [style*="background-color: #E85D04" i],
  [style*="background-color: #F45122" i],
  [style*="background-color: #FE993B" i],
  [style*="background-color: #FF7F2A" i],
  [style*="background-color: rgb(233, 130, 69)"],
  [style*="background-color: rgb(232, 93, 4)"],
  [style*="background-color: rgb(244, 81, 34)"],
  [style*="background-color: rgb(254, 153, 59)"],
  [style*="background-color: rgb(255, 127, 42)"]
) {
  border-radius: 999px !important;
}

/* Solid legacy CTAs become the approved warm gradient. */
:where(button, [role="button"], [aria-label], [tabindex="0"]):where(:not([data-cta="own"])):is(
  [style*="background-color: rgb(233, 130, 69)"],
  [style*="background-color: rgb(232, 93, 4)"],
  [style*="background-color: rgb(244, 81, 34)"],
  [style*="background-color: rgb(254, 153, 59)"],
  [style*="background-color: rgb(255, 127, 42)"],
  [style*="background-color: #E98245" i],
  [style*="background-color: #E9834B" i],
  [style*="background-color: #E85D04" i],
  [style*="background-color: #F45122" i],
  [style*="background-color: #FE993B" i]
) {
  background-image: var(--gradient-cta) !important;
  border-color: var(--ag-cta-outline, #F26A36) !important;
  box-shadow: var(--shadow-cta-soft) !important;
  transition: background-image 160ms ease, box-shadow 160ms ease, transform 100ms ease, opacity 160ms ease !important;
}

:where(button, [role="button"], [aria-label], [tabindex="0"]):where(:not([data-cta="own"])):is(
  [style*="background-color: rgb(233, 130, 69)"],
  [style*="background-color: rgb(232, 93, 4)"],
  [style*="background-color: rgb(244, 81, 34)"],
  [style*="background-color: rgb(254, 153, 59)"],
  [style*="background-color: #E98245" i],
  [style*="background-color: #E85D04" i],
  [style*="background-color: #F45122" i]
):hover {
  background-image: var(--gradient-cta-hover) !important;
  box-shadow: var(--shadow-cta) !important;
  transform: translateY(-1px);
}

:where(button, [role="button"], [aria-label], [tabindex="0"]):active {
  transform: translateY(1px);
}

:where(button, [role="button"], [aria-label], [tabindex="0"])[aria-disabled="true"],
:where(button, [role="button"]):disabled {
  box-shadow: none !important;
  cursor: not-allowed !important;
  opacity: 0.48 !important;
  transform: none !important;
}

/* Secondary dismiss actions share one warm outlined treatment. Components
   should expose their visible cancel label through aria-label; this bridge
   also keeps older RN-web modals visually consistent while they migrate. */
:where(button, [role="button"]):is(
  [aria-label="Отмена" i],
  [aria-label^="Не отменять" i],
  [aria-label="Cancel" i]
) {
  background: rgba(255, 247, 241, 0.72) !important;
  border: 1.5px solid var(--ag-cta-outline, #F26A36) !important;
  border-radius: 999px !important;
  color: #D9602F !important;
  box-shadow: 0 6px 18px rgba(214, 107, 67, 0.08) !important;
}

/* The empty post composer must still read as a button in dark mode. Its own
   muted capsule supplies the disabled treatment, so do not fade it twice. */
[data-testid="communityFeedPost"][aria-disabled="true"] {
  opacity: 0.74 !important;
}

/* Header account menu: CSS hover is deliberate here. RN-web's synthetic
   onHoverIn is not consistently emitted while the parent menu itself is kept
   open by mouse-enter/mouse-leave handlers. */
.ag-profile-menu-item {
  transition: background-color 160ms ease, color 160ms ease !important;
}

/* CORE-1234 — warm layered glass. This stays in web CSS because RN styles do
   not express backdrop blur, multi-stop backgrounds, or inset shadows. */
.ag-profile-menu-surface {
  background:
    radial-gradient(circle at 18% 8%, rgba(255, 255, 255, 0.7), transparent 44%),
    linear-gradient(145deg, rgba(255, 252, 247, 0.82), rgba(255, 241, 231, 0.74)) !important;
  border: 1px solid rgba(226, 151, 105, 0.32) !important;
  backdrop-filter: blur(26px) saturate(1.12);
  -webkit-backdrop-filter: blur(26px) saturate(1.12);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.7),
    0 22px 54px rgba(111, 65, 39, 0.14),
    0 4px 14px rgba(218, 123, 72, 0.08) !important;
}

html[data-theme="dark"] .ag-profile-menu-surface {
  background:
    radial-gradient(circle at 16% 7%, rgba(255, 170, 116, 0.1), transparent 43%),
    linear-gradient(145deg, rgba(47, 39, 37, 0.84), rgba(29, 26, 27, 0.78)) !important;
  border-color: rgba(242, 161, 112, 0.34) !important;
  box-shadow:
    inset 0 1px 0 rgba(255, 224, 204, 0.13),
    0 24px 58px rgba(8, 5, 4, 0.3),
    0 5px 18px rgba(225, 104, 56, 0.11) !important;
}

.ag-profile-menu-item:hover {
  background-color: rgba(255, 239, 229, 0.86) !important;
}

.ag-profile-menu-item:hover,
.ag-profile-menu-item:hover * {
  color: var(--ag-solar, #E98245) !important;
}

html[data-theme="dark"] .ag-profile-menu-item:hover {
  background-color: rgba(242, 106, 54, 0.14) !important;
}

.ag-profile-menu-item-danger:hover {
  background-color: rgba(229, 62, 62, 0.08) !important;
}

.ag-profile-menu-item-danger:hover,
.ag-profile-menu-item-danger:hover * {
  color: var(--color-error, #E53E3E) !important;
}

html[data-theme="dark"] .ag-profile-menu-item-danger:hover {
  background-color: rgba(252, 129, 129, 0.12) !important;
}

/* ═══ CORE-1178 — «Быстрые действия»: движение плиток ═══
   Анимации бесконечные и чисто декоративные, поэтому живут в CSS, а не в
   Animated: react-native-web пересчитывал бы их в JS на каждом кадре ради
   четырёх пульсирующих точек. Привязка — через `dataSet` (единственный
   документированный способ RNW дотянуться до DOM-атрибута), а не className:
   className в RNW не часть публичного API компонентов. */
@keyframes qa-pulse { 0% { transform: scale(1); opacity: .55 } 70%, 100% { transform: scale(2.1); opacity: 0 } }
@keyframes qa-dot   { 0%, 100% { opacity: 1 } 50% { opacity: .35 } }
@keyframes qa-pin   { 0%, 60%, 100% { transform: translateY(0) } 72% { transform: translateY(-5px) } 86% { transform: translateY(1px) } }
@keyframes qa-arrow { 0%, 55%, 100% { transform: translateY(0); opacity: 1 } 72% { transform: translateY(-6px); opacity: .25 } 73% { transform: translateY(5px); opacity: 0 } 88% { transform: translateY(0); opacity: 1 } }
@keyframes qa-sheen { 0% { transform: translateX(-22%) } 92%, 100% { transform: translateX(100%) } }
@keyframes qa-glow  { 0%, 100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0) } 50% { box-shadow: 0 0 22px -4px currentColor } }
@keyframes qa-spin  { to { transform: rotate(360deg) } }
@keyframes qa-sheen-seq { 0% { transform: translateX(-22%) } 25% { transform: translateX(100%) } 100% { transform: translateX(100%) } }

[data-qa-motion="pulse"] { animation: qa-pulse 2.2s ease-out infinite; }
[data-qa-motion="dot"]   { animation: qa-dot 1.4s ease-in-out infinite; }
[data-qa-motion="pin"]   { animation: qa-pin 3.2s ease-in-out infinite; }
[data-qa-motion="case"]  { animation: qa-pin 3.8s ease-in-out infinite; }
[data-qa-motion="arrow"] { animation: qa-arrow 3s ease-in-out infinite; }
[data-qa-motion="sheen"] { animation: qa-sheen 3s ease-in-out infinite; }

/* Эстафета блика в десктопной сетке: каждой карточке — своя четверть
   8-секундного круга, пробег 2s (первые 25% кадров qa-sheen-seq, задержка
   сдвигает его в свой слот). Кадры сужены до видимого диапазона (−22% → 100%
   ширины обёртки-карточки): полоса входит у левого края и уходит за правый,
   весь слот — видимый проход, и полоса «передаётся» следующей карточке без
   зазора. С макетными −130%/230% две трети слота полоса летела за краями
   невидимой — эффекта одного блика не получалось. Прошёл по последней — круг начинается заново.
   Слотов всегда четыре: с тремя карточками (веб без программ) после третьей
   остаётся тихая пауза-вдох, и это лучше, чем второй набор keyframes под
   каждый состав. На узких раскладках атрибут data-qa-seq не ставится — там
   блики идут одновременно (решение владельца, 2026-09-01). */
[data-qa-motion="sheen"][data-qa-seq] {
  animation-name: qa-sheen-seq;
  animation-duration: 8s;
  animation-timing-function: linear;
}
/* Задержки отрицательные: с положительными карточка до своего слота стоит без
   keyframes вовсе — полоса блика видна у левого края, а зритель ждёт, пока
   «начнётся». Сдвиг назад ставит ту же фазу (2i − 8), но анимация на загрузке
   уже идёт: первая карточка стартует сразу, мёртвого ожидания нет. */
[data-qa-seq="1"] { animation-delay: -6s; }
[data-qa-seq="2"] { animation-delay: -4s; }
[data-qa-seq="3"] { animation-delay: -2s; }
[data-qa-motion="glow"]  { animation: qa-glow 3.6s ease-in-out infinite; }
[data-qa-motion="spin"]  { animation: qa-spin 9s linear infinite; }

/* prefers-reduced-motion здесь сознательно НЕ выключает движение: у владельца
   Windows сообщает reduce (эффекты анимации выключены системно), а блок по его
   решению обязан двигаться везде (2026-09-01, CORE-1178). Это осознанное
   исключение из правила WORKING_RULES про декоративное движение. */

/* CORE-1735 — Live toolbar controls draw their own keyboard focus ring on the
   48px circle (spec: 2px, offset 3); the app-wide --focus-ring on the wrapper
   would add a second rectangle around glyph + label. */
[data-live-control]:focus-visible {
  box-shadow: none !important;
}

/* CORE-913: Live material adapted to the Practices helper only.
   Keep copy/artwork opaque and preserve the existing decorative orbits.
   The page backdrop is flat: tint/gradients preserve the glass appearance
   without a live backdrop blur/compositing pass. */
#practices-agapella-helper {
  --helper-surface: rgba(32, 34, 38, .76);
  --helper-warm: rgba(233, 130, 69, .055);
  --helper-edge: rgba(242, 161, 112, .42);
  --helper-top: rgba(255, 255, 255, .075);
  --helper-shadow: rgba(0, 0, 0, .20);
  --helper-fallback: #252629;
  --helper-rim: linear-gradient(125deg, rgba(242,161,112,.42), rgba(246,197,167,.20) 55%, rgba(215,183,164,.32));
  position: relative;
  isolation: isolate;
  background: transparent !important;
  border-color: var(--helper-edge) !important;
  box-shadow: 0 12px 32px var(--helper-shadow), inset 0 1px 0 var(--helper-top) !important;
}
[data-theme="light"] #practices-agapella-helper {
  --helper-surface: rgba(255, 248, 241, .55);
  --helper-warm: rgba(233, 130, 69, .025);
  --helper-edge: rgba(226, 151, 105, .38);
  --helper-top: rgba(255, 255, 255, .65);
  --helper-shadow: rgba(51, 42, 37, .10);
  --helper-fallback: #FFF8F1;
  --helper-rim: linear-gradient(125deg, rgba(226,151,105,.38), rgba(255,255,255,.65) 55%, rgba(226,151,105,.24));
}
#practices-agapella-helper::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: inherit;
  pointer-events: none;
  background: radial-gradient(ellipse at 100% 100%, var(--helper-warm), transparent 65%), var(--helper-surface);
}
@supports (mask-composite: exclude) or (-webkit-mask-composite: xor) {
  #practices-agapella-helper { border-color: transparent !important; }
  #practices-agapella-helper::after {
    content: "";
    position: absolute;
    inset: -1px;
    padding: 1px;
    border-radius: inherit;
    pointer-events: none;
    background: var(--helper-rim);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
  }
}
@media (prefers-reduced-transparency: reduce) {
  #practices-agapella-helper::before {
    background: var(--helper-fallback);
  }
}

/* CORE-1791 — «…» действий у сообщения в разделе сообщений.
   Кольцо фокуса рисуется на видимом квадрате 28 (2px #E98245, отступ 3), а не
   общим кольцом на зоне нажатия 44 — иначе вокруг глифа два прямоугольника. */
[data-msg-menu]:focus-visible {
  box-shadow: none !important;
}
[data-msg-menu]:focus-visible > [data-msg-menu-face] {
  /* !important — против общего сброса outline у всех элементов (начало файла). */
  outline: 2px solid #E98245 !important;
  outline-offset: 3px !important;
}
/* С мышью кнопка прозрачна до наведения на строку сообщения, фокуса внутри
   строки или открытого меню. Только opacity: display/visibility вынули бы её из
   порядка Tab и из дерева доступности. На тач-устройствах (hover:none или
   pointer:coarse) правило не действует — кнопка видна всегда, как была. */
@media (hover: hover) and (pointer: fine) {
  [data-msg-menu] {
    opacity: 0 !important;
  }
  [data-msg-row]:hover [data-msg-menu],
  [data-msg-row]:focus-within [data-msg-menu],
  [data-msg-menu][aria-expanded="true"] {
    opacity: 1 !important;
  }
}

/* ═══ LIVE ROOM SCROLLBARS (CORE-1735 slice 17, D-121, QA V08) ═══
   Any RN ScrollView / FlatList inside the Live room with `dataSet={{ liveScroll: 'true' }}`
   gets a thin rounded thumb in the room's palette; the room root names the theme through
   `data-live-theme`. Track transparent, 8px, no arrows. */
[data-live-scroll="true"] {
  scrollbar-width: thin;
  scrollbar-color: rgba(154, 66, 31, 0.28) transparent;
}

[data-live-scroll="true"]::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

[data-live-scroll="true"]::-webkit-scrollbar-track {
  background: transparent;
}

[data-live-scroll="true"]::-webkit-scrollbar-button {
  display: none;
  width: 0;
  height: 0;
}

[data-live-theme="light"] [data-live-scroll="true"]::-webkit-scrollbar-thumb {
  background: rgba(154, 66, 31, 0.28);
  border-radius: 4px;
}

[data-live-theme="light"] [data-live-scroll="true"]:hover::-webkit-scrollbar-thumb {
  background: rgba(154, 66, 31, 0.45);
}

[data-live-theme="dark"] [data-live-scroll="true"] {
  scrollbar-color: rgba(240, 160, 109, 0.32) transparent;
}

[data-live-theme="dark"] [data-live-scroll="true"]::-webkit-scrollbar-thumb {
  background: rgba(240, 160, 109, 0.32);
  border-radius: 4px;
}

[data-live-theme="dark"] [data-live-scroll="true"]:hover::-webkit-scrollbar-thumb {
  background: rgba(240, 160, 109, 0.5);
}

/* CORE-1828: lock the current roulette cycle without dimming its labels/icons. */
[data-testid="home-directions"] [aria-disabled="true"],
[data-testid="home-roulette-screen"] [aria-disabled="true"] {
  opacity: 1 !important;
}

/* A short invitation every three seconds; selection owns all motion once pressed. */
@keyframes home-prompt-shimmer {
  0%, 47.3684% { background-position: 100% 50%; text-shadow: 0 0 0 transparent; }
  72% { text-shadow: 0 0 8px rgba(255, 220, 150, .65); }
  94%, 100% { background-position: 0% 50%; text-shadow: 0 0 0 transparent; }
}
@keyframes home-petal-invitation {
  0%, 62%, 100% { fill-opacity: 0; }
  76% { fill-opacity: .13; }
  92% { fill-opacity: 0; }
}
@media (prefers-reduced-motion: no-preference) {
  [data-testid="home-directions"][data-roulette-phase="idle"][data-motion="full"] [data-testid="home-roulette-prompt"] {
    --home-prompt-ink: #594536;
    background-image: linear-gradient(110deg, var(--home-prompt-ink) 0%, var(--home-prompt-ink) 38%, #ad6928 46%, #fff4ca 50%, #e5a447 54%, var(--home-prompt-ink) 62%, var(--home-prompt-ink) 100%);
    background-size: 300% 100%;
    background-clip: text;
    -webkit-background-clip: text;
    color: transparent !important;
    animation: home-prompt-shimmer 3.8s ease-in-out infinite;
  }
  [data-testid="home-directions"][data-roulette-phase="idle"][data-motion="full"][data-home-theme="dark"] [data-testid="home-roulette-prompt"] { --home-prompt-ink: #fff3df; }
  [data-testid="home-directions"][data-roulette-phase="idle"][data-motion="full"] [data-testid^="home-petal-hint-"] {
    animation: home-petal-invitation 3s ease-in-out infinite;
  }
  [data-testid="home-petal-hint-1"] { animation-delay: 70ms !important; }
  [data-testid="home-petal-hint-2"] { animation-delay: 140ms !important; }
  [data-testid="home-petal-hint-3"] { animation-delay: 210ms !important; }
  [data-testid="home-petal-hint-4"] { animation-delay: 280ms !important; }
  [data-testid="home-petal-hint-5"] { animation-delay: 350ms !important; }
}

/* Only the chosen title and icon invite the next click; other labels keep their ink. */
@keyframes home-direction-icon-shimmer {
  0%, 60%, 100% { filter: brightness(1) drop-shadow(0 0 0 transparent); }
  77% { filter: brightness(1.4) drop-shadow(0 0 3px rgba(255, 225, 159, .6)); }
}
@media (prefers-reduced-motion: no-preference) {
  [data-roulette-selected="true"] [data-testid="home-direction-title"] {
    --home-direction-ink: #314C48;
    background-image: linear-gradient(110deg, var(--home-direction-ink) 0%, var(--home-direction-ink) 38%, #ad6928 46%, #fff4ca 50%, #e5a447 54%, var(--home-direction-ink) 62%, var(--home-direction-ink) 100%);
    background-size: 300% 100%;
    background-clip: text;
    -webkit-background-clip: text;
    color: transparent !important;
    animation: home-prompt-shimmer 3.8s ease-in-out infinite;
  }
  [data-roulette-selected="true"][data-home-theme="dark"] [data-testid="home-direction-title"] { --home-direction-ink: #E0E7DC; }
  [data-roulette-selected="true"] [data-testid="home-direction-icon"] { animation: home-direction-icon-shimmer 3s ease-in-out infinite; }
}

/* Begin the selected cue after 1.5s in both themes; keep the idle invitation unchanged. */
@keyframes home-selected-title-shimmer {
  0%, 44.1176% { background-position: 100% 50%; text-shadow: 0 0 0 transparent; }
  72% { text-shadow: 0 0 8px rgba(255, 220, 150, .65); }
  96%, 100% { background-position: 0% 50%; text-shadow: 0 0 0 transparent; }
}
@keyframes home-selected-icon-shimmer {
  0%, 44.1176%, 100% { filter: brightness(1) drop-shadow(0 0 0 transparent); }
  72% { filter: brightness(1.4) drop-shadow(0 0 3px rgba(255, 225, 159, .6)); }
}
@media (prefers-reduced-motion: no-preference) {
  [data-roulette-selected="true"][data-home-theme] [data-testid="home-direction-title"] {
    animation: home-selected-title-shimmer 3.4s ease-in-out infinite;
  }
  [data-roulette-selected="true"][data-home-theme] [data-testid="home-direction-icon"] {
    animation: home-selected-icon-shimmer 3.4s ease-in-out infinite;
  }
}

/* The approved path invitation stays light in both themes, including its ink. */
html[data-theme] [data-testid="home-path-title"] {
  color: #314C48 !important;
}

/* Light petals need a stronger pearl flash against the bright landscape. */
@keyframes home-petal-invitation-light {
  0%, 62%, 100% { fill-opacity: 0; filter: drop-shadow(0 0 0 transparent); }
  76% { fill-opacity: .34; filter: drop-shadow(0 0 4px rgba(255, 208, 125, .28)); }
  92% { fill-opacity: 0; filter: drop-shadow(0 0 0 transparent); }
}
@media (prefers-reduced-motion: no-preference) {
  [data-testid="home-directions"][data-roulette-phase="idle"][data-motion="full"][data-home-theme="light"] [data-testid^="home-petal-hint-"] {
    fill: #FFF3B8;
    animation-name: home-petal-invitation-light;
  }
}
/* Keep the road in the wide crop; the phone retains its approved composition. */
@media (min-width: 768px) {
  [data-testid="home-path-artwork"] {
    object-position: center 78%;
  }
}

/* Assistant is a second action: a persistent underline, with interaction-only light. */
[data-testid="home-flower-assistant-label"] {
  text-decoration-line: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 3px;
  text-decoration-color: rgba(122, 83, 46, .55);
}
[data-home-theme="dark"] [data-testid="home-flower-assistant-label"] {
  text-decoration-color: rgba(255, 224, 177, .65);
}
/* Keep the hit area invisible: only the label carries the interaction cue. */
[data-testid="home-flower-assistant"]:hover,
[data-testid="home-flower-assistant"]:focus-visible,
[data-testid="home-flower-assistant"]:active {
  background-color: transparent;
  box-shadow: none !important;
}
[data-testid="home-flower-assistant"]:focus-visible {
  outline: none;
}
[data-testid="home-flower-assistant"]:focus-visible [data-testid="home-flower-assistant-label"] {
  text-decoration-thickness: 2px;
}

/* Link text itself brightens on interaction, with no autonomous shimmer. */
[data-testid="home-flower-assistant-label"] {
  transition: color 160ms ease, text-shadow 160ms ease, text-decoration-color 160ms ease;
}
[data-testid="home-flower-assistant"]:hover [data-testid="home-flower-assistant-label"],
[data-testid="home-flower-assistant"]:focus-visible [data-testid="home-flower-assistant-label"] {
  color: #8B5C32 !important;
  text-shadow: 0 0 8px rgba(255, 251, 222, .9);
  text-decoration-color: currentColor;
}
[data-home-theme="dark"] [data-testid="home-flower-assistant"]:hover [data-testid="home-flower-assistant-label"],
[data-home-theme="dark"] [data-testid="home-flower-assistant"]:focus-visible [data-testid="home-flower-assistant-label"] {
  color: #FFF9DE !important;
  text-shadow: 0 0 8px rgba(255, 219, 153, .55);
}
@media (prefers-reduced-motion: reduce) {
  [data-testid="home-flower-assistant-label"] { transition: none; }
}

/* Mobile inline roulette: calm, readable ink over the pearl selection wash. */
[data-testid="home-directions-grid"] [data-roulette-selected="true"] [data-testid="home-direction-title"] {
  animation: none;
  background-image: none;
  color: #314C48 !important;
  text-shadow: none;
}
[data-testid="home-directions-grid"] [data-roulette-selected="true"][data-home-theme="dark"] [data-testid="home-direction-title"] {
  color: #FFF8E7 !important;
}

/* Mobile click cues: animate letters only; retain the approved soft card wash. */
@media (prefers-reduced-motion: no-preference) {
  [data-testid="home-directions"][data-motion="full"][data-roulette-phase="idle"] [data-testid="home-mobile-roulette-prompt"],
  [data-testid="home-directions"][data-motion="full"] [data-testid="home-directions-grid"] [data-roulette-selected="true"] [data-testid="home-direction-title"] {
    --home-mobile-ink: #314C48;
    background-image: linear-gradient(110deg, var(--home-mobile-ink) 0%, var(--home-mobile-ink) 38%, #AD783F 46%, #FFF4CA 50%, #D4A05A 54%, var(--home-mobile-ink) 62%, var(--home-mobile-ink) 100%);
    background-size: 300% 100%;
    background-clip: text;
    -webkit-background-clip: text;
    color: transparent !important;
    animation: home-selected-title-shimmer 3.4s ease-in-out infinite;
  }
  [data-testid="home-directions"][data-motion="full"][data-roulette-phase="idle"] [data-testid="home-mobile-roulette-prompt"] {
    --home-mobile-ink: #3D352B;
    animation-name: home-prompt-shimmer;
    animation-duration: 3.8s;
  }
  [data-testid="home-directions"][data-motion="full"][data-home-theme="dark"] [data-testid="home-directions-grid"] [data-roulette-selected="true"] [data-testid="home-direction-title"] {
    --home-mobile-ink: #FFF8E7;
  }
}

/* Confirmed follow is a status, not an unavailable action to fade out. */
[data-testid="home-master-following"][aria-disabled="true"]:not([aria-busy="true"]) {
  opacity: 1 !important;
  cursor: default;
}

/* Short landscape viewports keep every auth action reachable by scrolling. */
[data-testid="auth-cta-card"] {
  max-height: calc(100vh - 32px);
  max-height: calc(100dvh - 32px);
  overflow-y: auto !important;
  overscroll-behavior-y: contain;
}
